import { FC, Fragment, HTMLAttributes, useEffect, useState } from "react";
import { PhotoIcon, TrashIcon } from "@heroicons/react/24/solid";
import { zodResolver } from "@hookform/resolvers/zod";
import { LocationFormProps } from "data/reduxInterfaces";
import { SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import { useAppDispatch } from "hooks/hooks";
import toast from "react-hot-toast";

const MAX_FILE_SIZE = 1024 * 1024 * 10;
const ACCEPTED_IMAGE_MIME_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];

const formSchema = z.object({
  newImages: z
    .array(
      z.instanceof(File, {
        message: "L'image est obligatoire.",
      })
    )
    .refine((files) => {
      return files.every((file) => file?.size <= MAX_FILE_SIZE);
    }, "Le fichier ne doit pas dépasser 10MB.")
    .refine((files) => {
      return files.every((file) => ACCEPTED_IMAGE_MIME_TYPES.includes(file?.type));
    }, "Le fichier doit être une image au format JPEG, PNG ou GIF."),
});

const LocationImages: FC<LocationFormProps & HTMLAttributes<HTMLDivElement>> = ({
  isLoading,
  handleForm,
  data,
  FormData,
}) => {
  const dispatch = useAppDispatch();

  const {
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
    trigger,
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      newImages: data?.newImages ?? [],
    },
    mode: "onSubmit",
  });

  const [images, setImages] = useState<string[]>([]);
  const [oldImages, setOldImages] = useState<string[]>([]);
  const [imageError, setImageError] = useState<string>("");

  useEffect(() => {
    if (data) {
      setImages(data.images);
    }
  }, [data]);

  useEffect(() => {
    if (handleForm.type === "chekLocationFormEight") {
      handleSubmit(onSubmit)();
    }
  }, [handleForm]);

  const handleAddImage = (files: FileList | null) => {
    if (files) {
      const fileArray = Array.from(files);
      setValue("newImages", [...getValues("newImages"), ...fileArray]);
      trigger("newImages");
      // Réinitialiser l'erreur d'image quand on ajoute une image
      setImageError("");
    }
  };

  const handleDeleteImage = (index: number) => {
    const images = [...(getValues("newImages") || [])];
    images.splice(index, 1);
    setValue("newImages", images);
    trigger("newImages");
  };

  const handleDeleteOldImage = (index: number) => {
    setImages(data?.images.filter((_, i) => i !== index) ?? []);

    const imageToRemove = data?.images.filter((_, i) => i === index) ?? [];
    setOldImages([...oldImages, ...imageToRemove]);
  };

  const validateTotalFileSize = (files: File[]) => {
    const totalSize = files.reduce((acc, file) => acc + file.size, 0);
    return totalSize <= MAX_FILE_SIZE;
  };

  const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = async (data) => {
    const newImages = getValues("newImages");
    const totalImages = (images?.length || 0) + (newImages?.length || 0);

    // Vérifier qu'il y a au moins une image au total
    if (totalImages === 0) {
      setImageError("Vous devez ajouter au moins une image de votre espace.");
      toast.error("Vous devez ajouter au moins une image de votre espace.");
      return;
    }

    // Réinitialiser l'erreur si validation OK
    setImageError("");

    if (!validateTotalFileSize(newImages)) {
      toast.error("La taille totale des fichiers ne doit pas dépasser 10 Mo.");
      return;
    }

    FormData({ oldImages: oldImages, ...data });
  };

  // const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = async (data) => {
  //   FormData({ oldImages: oldImages, ...data });
  // };

  return (
    <Fragment>
      <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
        <div className="w-full">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">📸 Images de votre espace</h2>
            <p className="text-gray-600 dark:text-gray-400">
              Ajoutez des photos attrayantes pour présenter votre espace sous son meilleur jour
            </p>
            <div className="border-b-2 w-20 border-eventToBee mt-4"></div>
          </div>

          {/* Section Images */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8">
            <div className="flex items-center mb-6">
              <PhotoIcon className="w-6 h-6 text-eventToBee mr-3" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Galerie d'images</h3>
            </div>

            {/* Images existantes et nouvelles */}
            {(images?.length > 0 || getValues("newImages")?.length > 0) && (
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Vos images :</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {images?.map((image, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={process.env.REACT_APP_CLOUD_FRONT + image}
                        alt={`Image ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg border border-gray-200 dark:border-gray-600"
                      />
                      <button
                        type="button"
                        onClick={() => handleDeleteOldImage(index)}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))}

                  {getValues("newImages")?.map((image: File, index: any) => (
                    <div key={`new-${index}`} className="relative group">
                      <img
                        src={URL.createObjectURL(image)}
                        alt={`Nouvelle image ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg border border-gray-200 dark:border-gray-600"
                      />
                      <button
                        type="button"
                        onClick={() => handleDeleteImage(index)}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Zone de téléchargement */}
            <div
              className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-8 text-center hover:border-eventToBee transition-colors"
              onDrop={(e) => {
                e.preventDefault();
                handleAddImage(e.dataTransfer.files);
              }}
              onDragOver={(e) => {
                e.preventDefault();
              }}
              onDragEnter={(e) => {
                e.preventDefault();
              }}
            >
              <div className="space-y-4">
                <div className="flex justify-center">
                  <PhotoIcon className="w-12 h-12 text-gray-400 dark:text-gray-500" />
                </div>
                <div>
                  <label
                    htmlFor="fileInput"
                    className="cursor-pointer inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-eventToBee hover:bg-eventToBee/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-eventToBee transition-colors"
                  >
                    Choisir des images
                    <input
                      id="fileInput"
                      name="images"
                      type="file"
                      multiple
                      className="sr-only"
                      onChange={(e) => handleAddImage(e.target.files)}
                      accept="image/jpeg, image/jpg, image/png, image/webp"
                    />
                  </label>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">ou glissez-déposez vos fichiers ici</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">PNG, JPG, GIF jusqu'à 10MB par fichier</p>
              </div>
            </div>

            {errors.newImages?.message && (
              <div className="mt-3 text-sm text-red-600 dark:text-red-400">{errors.newImages.message}</div>
            )}

            {imageError && (
              <div className="mt-3 text-sm text-red-600 dark:text-red-400 flex items-center space-x-1">
                <span>⚠️</span>
                <span>{imageError}</span>
              </div>
            )}
          </div>
        </div>
      </form>
    </Fragment>
  );
};

export default LocationImages;
